import React, { useEffect, useState } from "react";
import Layout from "../components/layout/Layout";
import axios from "../config";
import Pagination from "../components/common/Pagination";
import { useNavigate } from "react-router-dom";
import Button from "../components/common/Button";
import { Download } from "lucide-react";
import { saveAs } from "file-saver";
import Spinner from "../components/common/Spinner";

const questions = [
  "Which environment is hardest for you to recover today?",
  // "How confident are you that you can restore clean systems (not reinfected) under pressure?",
  "How many VMs or servers does your environment protect?",
  "What is your current DR or backup setup?",
  "When was your last end-to-end recovery test of a critical application?",
  "How confident are you that you can restore clean systems (not reinfected) under pressure?",
];

const ZertoLeadsPage = () => {
  const [leadsData, setLeadsData] = useState([]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(leadsData.length === 0);
  const token = localStorage.getItem("token");

  useEffect(() => {
    setIsLoading(true);
    axios
      .get("/zerto/get-leads", {
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
        },
      })
      .then((response) => {
        setLeadsData(response.data.reverse());
      })
      .catch((error) => {
        if (error.status === 403 || error.status === 401) {
          localStorage.removeItem("token");
          navigate("/auth");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, navigate]);

  const [currentPage, setCurrentPage] = useState(1);

  const leadsPerPage = 10;
  const totalPages = Math.ceil(leadsData.length / leadsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const formattedLeadsData = leadsData.map((lead) => {
    const date = new Date(lead.created_at);

    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const formattedDate = date.toLocaleString(undefined, options);

    return {
      ...lead,
      timestamp: formattedDate,
    };
  });

  const handleDownloadCSV = () => {
    const csvData = formattedLeadsData.map((lead, index) => {
      const lastName = lead.last_name ? lead.last_name : "-";

      return `${lead.first_name},${lastName},${lead.email},${lead.phone},${lead.company},${lead.designation},${lead.country},${lead.environment},${lead.vms_environment_protect},${lead.current_dr},${lead.recovery_test},${lead.confidence_under_pressure},"${lead.timestamp}"`;
    });

    const csvContent = [
      `First Name,Last Name,Email,Phone,Company,Designation,Country,${questions.join(
        ",",
      )},Time Stamp`,
    ]
      .concat(csvData)
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `zerto_leads${new Date().getTime()}.csv`);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/auth");
  };

  return (
    <Layout title="Zerto Leads">
      <div className="leads-page min-h-screen">
        <div className="leads-masthead-container bg-blue-06">
          <div className="container">
            <div className="d-flex justify-content-between">
              <h1 className="text-white"> Zerto Leads </h1>
            </div>
          </div>
        </div>
        <div className="container mx-auto">
          {!isLoading ? (
            leadsData.length ? (
              <>
                <div className="my-6 flex justify-end gap-4">
                  <div>
                    <Button
                      text="Download CSV"
                      Icon={Download}
                      onClick={handleDownloadCSV}
                    />
                  </div>
                  <div>
                    <Button text="Logout" onClick={handleLogout} />
                  </div>
                </div>
                <div className="leads-data-container mb-4">
                  <div className="leads-data-head">
                    <div className="grid grid-cols-5 gap-6">
                      <div className="col">
                        <p className="b3 text-grey-05 p-2">
                          Name, Email and Phone
                        </p>
                      </div>
                      <div className="col">
                        <p className="b3 text-grey-05 p-2">
                          Company and Designation
                        </p>
                      </div>
                      <div className="col">
                        <p className="b3 text-grey-05 p-2">Answers</p>
                      </div>
                      <div className="col">
                        <p className="b3 text-grey-05 p-2">UTMs</p>
                      </div>
                      <div className="col">
                        <p className="b3 text-grey-05 p-2">Time stamp</p>
                      </div>
                    </div>
                    <div className="border border-b-grey-04 mb-4"></div>
                    <div className="leads-data-body">
                      {formattedLeadsData
                        .slice(
                          (currentPage - 1) * leadsPerPage,
                          currentPage * leadsPerPage,
                        )
                        .map((lead) => {
                          return (
                            <div
                              key={lead.id}
                              className="leads-data border-b border-b-grey-04 py-4"
                            >
                              <div className="grid grid-cols-5 gap-6 mb-6">
                                <div className="col">
                                  <p className="b3 text-grey-08 p-2 b4">
                                    <span className="capitalize">
                                      {lead.first_name} {lead.last_name}{" "}
                                    </span>
                                    <br />
                                    <span className="text-grey-04">
                                      {lead.email}
                                    </span>
                                    <br />
                                    <span className="text-grey-04">
                                      {lead.phone ? `(${lead.phone})` : ""}
                                    </span>
                                  </p>
                                </div>
                                <div className="col">
                                  <p className="b3 text-grey-08 p-2 b4 capitalize">
                                    <span>{lead.company}</span>
                                    <br />
                                    <span className="text-grey-04">
                                      {lead.designation}
                                    </span>
                                  </p>
                                </div>
                                <div className="col">
                                  <div className="flex flex-col gap-3">
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        {questions[0]}
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.environment || "-"}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        {questions[1]}
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.vms_environment_protect || "-"}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        {questions[2]}
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.current_dr || "-"}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        {questions[3]}
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.recovery_test || "-"}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        {questions[4]}
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.confidence_under_pressure || "-"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="col">
                                  <div className="flex flex-col gap-3">
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        Source
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.utm_source || "-"}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        Medium
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.utm_medium || "-"}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <p className="b4 text-xs text-grey-05">
                                        Campaign
                                      </p>
                                      <p className="b3 text-sm font-semibold text-grey-08">
                                        {lead.utm_campaign || "-"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="col">
                                  <p className="b3 text-grey-08 p-2 b4">
                                    {lead.timestamp || "-"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>

                <div className="pagination-container mb-8">
                  <Pagination
                    pages={totalPages}
                    handlePageChange={handlePageChange}
                    activePage={totalPages ? currentPage - 1 : -1}
                  />
                </div>
              </>
            ) : (
              <p className="b2 text-grey-08 py-10">No Leads</p>
            )
          ) : (
            <div className="flex justify-center py-10">
              <div className="flex items-center gap-4">
                <Spinner styles="text-blue-05" size={60} />
                <p className="b2 text-blue-05">Fetching Leads...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ZertoLeadsPage;
